import * as React from 'react';
import mapbox from 'mapbox-gl';
import { withCoordinates } from '../../store/StoreProvider';
import { drawRoute } from './helpers';
import { Switch, Route, Redirect, withRouter, RouteComponentProps } from 'react-router-dom';
import { PrivateRoute } from '../../App';

import 'mapbox-gl/dist/mapbox-gl.css';
import AppStore from '../../store/AppStore';
import Profile from '../Profile';
import Header from '../../shared/Header';
import Order from './Order';
import Hidden from '@material-ui/core/Hidden';
import { Theme, withStyles } from '@material-ui/core/styles';
import { Styles } from '@material-ui/core/styles/withStyles';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '../../shared/MenuIcon';
import CustomDrawer from './Drawer';

type Props = {
  classes?: any;
  coordinates?: string[];
  resetCoords?: AppStore['resetCoordinates'];
}

const styles: Styles<Theme, Props, string> = (theme: Theme) => ({
  map: {
    position: "absolute",
    top: '73px',
    right: 0,
    left: 0,
    bottom: 0,
    width: "100%",
    [theme.breakpoints.down('xs')]: {
      top: 0
    }
  },
  section: {
    position: 'relative',
    height: 'calc(100vh - 73px)',
    pointerEvents: 'none',
    [theme.breakpoints.down('xs')]: {
      height: '100vh'
    }
  },
  menuIcon: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      position: 'absolute',
      top: '58px',
      left: '10px',
      zIndex: 150
    }
  },
})

class Map extends React.Component<Props & RouteComponentProps, { isOpen: boolean }> {
  ref = React.createRef<HTMLDivElement | null>();
  state = {
    isOpen: false
  };

  map: mapbox.Map = null;

  componentDidMount(){
    mapbox.accessToken = "pk.eyJ1IjoicGFzaGEtcmVlIiwiYSI6ImNrY3dkM3NpZzBjdXYzNG1vejg1bmthemgifQ.AGJze9GF-B-HSOEiEomndw";

    this.map = new mapbox.Map({
        container: this.ref.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [30.3056504, 59.9429126],          
        zoom: 10
    });

    this.map.setStyle('mapbox://styles/mapbox/light-v10');

    if (this.props.coordinates && this.props.coordinates.length && this.map) {
      this.map.on('load', () => this.drawPath(this.props.coordinates));
    }
  }

  componentDidUpdate(prevProps) {
    const { coordinates } = this.props;
    const { coordinates: prevCoords } = prevProps;
    if (prevCoords !== coordinates) {
      this.drawPath(coordinates);
    }
  }

  componentWillUnmount(){
    if (this.map) {
      this.map.remove()
    }
  }

  render(): JSX.Element {
    const { classes, match: { path } } = this.props;
    const { isOpen } = this.state;

    return (
      <div>
        <Hidden only='xs'>
          <Header />
        </Hidden>
        <IconButton className={classes.menuIcon} onClick={this.toggleDrawer}>
          <MenuIcon />
        </IconButton>
        <CustomDrawer isOpen={isOpen} toggle={this.toggleDrawer} />
        <div className={classes.map} ref={this.ref} />
        <section className={classes.section}>
          <Switch>
            <PrivateRoute path={`${path}/order`} component={Order} />
            <PrivateRoute path={`${path}/profile`} component={Profile} />
            <Route path={path}>
              <Redirect to={`${path}/order`} />
            </Route>
          </Switch>
        </section>
      </div>
    );
  }

  private toggleDrawer = (): void => {
    this.setState((state) => ({ isOpen: !state.isOpen }));
  }

  private drawPath(coordinates: string[]): void {
    if (this.map && this.map.getLayer('route')) {
      this.map.flyTo({
        center: [30.3056504, 59.9429126],
        zoom: 10
      });
      this.map.removeLayer("route");
      this.map.removeLayer("start1");
      this.map.removeLayer("start2");
      this.map.removeLayer("start3");
      this.map.removeLayer("end1");
      this.map.removeLayer("end2");
      this.map.removeLayer("end3");
      this.map.removeSource("route");
      this.map.removeSource("circle-start");
      this.map.removeSource("circle-end");
    }

    if (this.map && this.map.isStyleLoaded() && coordinates.length) {
      drawRoute(this.map, coordinates);
    }
  }
}

export default withRouter(withCoordinates(withStyles(styles, { withTheme: true })(Map)));
